// Packages
import React from "react";
import { Box } from "@mui/material";

// components
import { createColumnHelper } from "@tanstack/react-table";
import CheckboxColumn from "components/Table/CheckboxColumn";
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";
import { formatTrafficBytes } from "utils/format";

const columnHelper = createColumnHelper();

const getAccessPointsColumns = (
  showCheckbox = true,
  portTagsMap
) => {
  const baseColumns = [
    /* settings */
    columnHelper.accessor("deviceName", {
      header: "Device Name",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor("portIndex", {
      header: "Port",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor("state", {
      id: "state",
      header: "State",
      cell: (info) => {
        if (!info.column) {
          return <Box>{info.getValue()}</Box>;
        }
        const value = info.row.original.state;

        if (!value || value === "-") {
          return <Box>-</Box>;
        }

        if (
          React.isValidElement(value) &&
          value.type === "img" &&
          !value.props.src
        ) {
          return <Box>-</Box>;
        }

        return <Box>{value}</Box>;
      },
    }),
    columnHelper.accessor("portProfileName", {
      header: "Port Profile",
      cell: (info) => {
        const value = info.getValue();
        const isProfileValid = value != null && value !== "No Profile";

        return <EllipsisTooltipCell text={isProfileValid ? value : "-"} />
      },
    }),
    columnHelper.accessor("PVID", {
      header: "PVID",
      cell: (info) => {
        const selectedProfile = info.row.original.selectedProfile;
        const value = selectedProfile?.PVID ?? info.getValue() ?? "-";
        return <Box>{value}</Box>
      },
    }),
    columnHelper.accessor((row) => {
      const allowVlan = row.allowVlan;
      if (Array.isArray(allowVlan)) {
        if (allowVlan.length === 0) return "-";
        else return allowVlan.join(", ");
      } else {
        return allowVlan ?? "-";
      }

    }, {
      id: "allowVlan",
      header: "Allowed VLANS",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor((row) => {
      const tagNames = row.portTags.map(tagId => portTagsMap[tagId] ?? null).filter(Boolean);
      return tagNames.length > 0 ? tagNames.join(", ") : "-";
    }, {
      id: "tagsLabel",
      header: "Tags",
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
    /* status */
    columnHelper.accessor("modelName", {
      header: "Model Name",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("lldp", {
      header: "LLDP",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("deviceIp", {
      header: "Device Ip",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),

    /* statistic */
    columnHelper.accessor("txTraffic", {
      header: "Tx Traffic",
      cell: (info) => <Box>{info.getValue() ? formatTrafficBytes(info.getValue()) : "-"}</Box>,
    }),
    columnHelper.accessor("rxTraffic", {
      header: "Rx Traffic",
      cell: (info) => <Box>{info.getValue() ? formatTrafficBytes(info.getValue()) : "-"}</Box>,
    }),
    columnHelper.accessor("txMulticast", {
      header: "Tx Multicast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("rxMulticast", {
      header: "Rx Multicast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("txBroadcast", {
      header: "Tx Broadcast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),

    columnHelper.accessor("rxBroadcast", {
      header: "Rx Broadcast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("txPackets", {
      header: "Tx Packets",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("rxPackets", {
      header: "Rx Packets",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("totalTraffic", {
      header: "Total Traffic",
      cell: (info) => <Box>{info.getValue() ? formatTrafficBytes(info.getValue()) : "-"}</Box>,
    }),
  ];
  return showCheckbox
    ? [CheckboxColumn(columnHelper), ...baseColumns]
    : baseColumns;
};

export default getAccessPointsColumns;
